import React, { useState, useEffect } from 'react';
import { Box, Typography, Grid, Button, CircularProgress } from '@mui/material';
import FactoryIcon from '@mui/icons-material/Factory';
import AgricultureIcon from '@mui/icons-material/Agriculture';
import CorporateFareIcon from '@mui/icons-material/CorporateFare';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const HomeDashboard = () => {
  // --- NAVIGATION STATE ---
  // 'idle' = Shows 3 Cards
  // 'otp-login' = Shows Mobile/OTP form for Product/Office
  // 'aadhaar-verify' = Shows Aadhaar input for Farmer
  // 'aadhaar-register' = Shows Full registration form for new Farmer
  const [authMode, setAuthMode] = useState('idle');
  const [activeRole, setActiveRole] = useState('');

  // --- FORM STATES ---
  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState('');
  const [showOtpInput, setShowOtpInput] = useState(false);
  
  const [aadhaar, setAadhaar] = useState('');
  const [regData, setRegData] = useState({ name: '', phone: '', password: '' });
  
  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(60);

  // Timer effect for OTP
  useEffect(() => {
    let interval;
    if (timer > 0 && showOtpInput) {
      interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [timer, showOtpInput]);

  // --- RESET FUNCTION ---
  const handleBackToHome = () => {
    setAuthMode('idle');
    setActiveRole('');
    setMobile('');
    setOtp('');
    setShowOtpInput(false);
    setAadhaar('');
    setRegData({ name: '', phone: '', password: '' });
    setTimer(60);
  };

  // --- MOCK HANDLERS (Backend will be plugged here later) ---

  const handleCardClick = (cardId) => {
    if (cardId === 'farmer') {
      setActiveRole('Farmer');
      setAuthMode('aadhaar-verify');
    } else {
      setActiveRole(cardId === 'product' ? 'Production Center' : 'Office');
      setAuthMode('otp-login');
      setShowOtpInput(false);
    }
  };

  const handleSendOtp = (e) => {
    e.preventDefault();
    if (mobile.length !== 10) return toast.error("Enter valid 10-digit number");
    setLoading(true);
    setTimeout(() => {
      toast.success(`Mock: OTP sent to +91 ${mobile}`);
      setShowOtpInput(true);
      setTimer(60);
      setLoading(false);
    }, 1000);
    
  };

  const handleVerifyOtp = (e) => {
    e.preventDefault();
    if (otp.length < 4) return toast.error("Enter valid OTP");
    setLoading(true);
    setTimeout(() => {
      toast.success(`Mock: Logged in successfully as ${activeRole}!`);
      setLoading(false);
      // navigate('/dashboard') will go here
    }, 1000);
  };

  const handleVerifyAadhaar = (e) => {
    e.preventDefault();
    if (aadhaar.replace(/\D/g, '').length !== 12) return toast.error("Enter valid 12-digit Aadhaar");
    setLoading(true);
    
    // MOCK LOGIC: If aadhaar ends with 0, trigger registration. Else trigger success.
    setTimeout(() => {
      if (aadhaar.endsWith('0')) {
        toast.info("Aadhaar found in DB. Please complete registration.");
        setAuthMode('aadhaar-register');
      } else {
        toast.success("Mock: Aadhaar Verified! Logged in as Farmer.");
        // navigate('/farmer-dashboard') will go here
      }
      setLoading(false);
    }, 1500);
  };

  const handleFarmerRegister = (e) => {
    e.preventDefault();
    if (!regData.name || !regData.phone || !regData.password) return toast.error("All fields are required");
    setLoading(true);
    setTimeout(() => {
      toast.success("Mock: Farmer Registered & Logged in!");
      setLoading(false);
      // navigate('/farmer-dashboard') will go here
    }, 1500);
  };

  // --- REUSABLE STYLES ---
  const inputStyle = { 
    width: '100%', padding: '12px 0', fontSize: '16px', border: 'none', 
    borderBottom: '2px solid #1b5e20', outline: 'none', background: 'transparent',
    color: '#333', boxSizing: 'border-box'
  };

  // --- CARDS DATA ---
  const dashboardCards = [
    { id: 'product', title: 'Production Center', icon: <FactoryIcon sx={{ fontSize: 50, color: '#ffc022' }} />, gradient: 'linear-gradient(135deg, #004d40 0%, #00695c 100%)' },
    { id: 'farmer', title: 'Farmer', icon: <AgricultureIcon sx={{ fontSize: 50, color: '#ffc022' }} />, gradient: 'linear-gradient(135deg, #1b5e20 0%, #2e7d32 100%)' },
    { id: 'office', title: 'Office', icon: <CorporateFareIcon sx={{ fontSize: 50, color: '#ffc022' }} />, gradient: 'linear-gradient(135deg, #0d3b1e 0%, #1b5e20 100%)' }
  ];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        background: 'linear-gradient(rgba(0, 30, 0, 0.92), rgba(0, 51, 0, 0.95)), url("/public/img 3.jpg")',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative'
      }}
    >
      <ToastContainer position="top-center" autoClose={2000} theme="colored" />

      {/* --- TOP HEADER --- */}
      <Box sx={{ p: { xs: 2, md: 4 }, display: 'flex', alignItems: 'center', gap: 2 }}>
        <img src="/TN.png" alt="TN Gov Logo" style={{ width: 55, height: 55, objectFit: 'contain', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))' }} />
        <Box>
          <Typography variant="h5" sx={{ color: '#ffc022', fontWeight: 800, lineHeight: 1.2, textShadow: '0 2px 4px rgba(0,0,0,0.4)' }}>
            தமிழ்நாடு வேளாண் வனவியல்
          </Typography>
          <Typography variant="body" sx={{ color: '#ffc022', fontWeight: 500, letterSpacing: '1.5px', textShadow: '0 2px 4px rgba(0,0,0,0.4)' }}>
            TN AGROFORESTRY
          </Typography>
        </Box>
      </Box>

      {/* --- CENTER CONTENT AREA --- */}
      <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3 }}>
        
        {/* VIEW 1: DASHBOARD CARDS */}
        {authMode === 'idle' && (
          <Grid container spacing={4} maxWidth="900px">
            {dashboardCards.map((card) => (
              <Grid item xs={12} sm={4} key={card.id}>
                <Box
                  onClick={() => handleCardClick(card.id)}
                  sx={{
                    cursor: 'pointer', borderRadius: 4, background: card.gradient,
                    padding: '3rem 1.5rem', textAlign: 'center',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    border: '1px solid rgba(255, 192, 34, 0.1)',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.4)',
                    '&:hover': {
                      transform: 'translateY(-12px)',
                      boxShadow: '0 20px 40px rgba(255, 192, 34, 0.3)',
                      border: '1px solid #ffc022'
                    }
                  }}
                >
                  <Box sx={{ mb: 2 }}>{card.icon}</Box>
                  <Typography variant="h5" fontWeight={700} sx={{ color: '#ffffff', letterSpacing: '1px' }}>{card.title}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        )}

        {/* VIEW 2 & 3: AUTHENTICATION FORMS */}
        {authMode !== 'idle' && (
          <Box sx={{ width: '100%', maxWidth: 420 }}>
            <Box sx={{ bgcolor: 'rgba(255,255,255,0.97)', borderRadius: 4, p: 4, boxShadow: '0 10px 30px rgba(0,0,0,0.5)' }}>
              
              {/* Back Button */}
              <Button startIcon={<ArrowBackIcon />} onClick={handleBackToHome} sx={{ mb: 2, color: '#555', pl: 0, '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' } }}>
                Back
              </Button>

              {/* Form Title */}
              <Typography variant="h5" sx={{ mb: 0.5, fontWeight: 700, color: '#1b5e20', textAlign: 'center' }}>
                {authMode === 'otp-login' && !showOtpInput && `Sign In as ${activeRole}`}
                {authMode === 'otp-login' && showOtpInput && 'Verify OTP'}
                {authMode === 'aadhaar-verify' && 'Farmer Verification'}
                {authMode === 'aadhaar-register' && 'Farmer Registration'}
              </Typography>
              <Typography variant="body2" sx={{ mb: 3, color: 'gray', textAlign: 'center' }}>
                {authMode === 'otp-login' && !showOtpInput && 'Enter your registered mobile number'}
                {authMode === 'otp-login' && showOtpInput && `OTP sent to +91 ${mobile}`}
                {authMode === 'aadhaar-verify' && 'Enter your Agro linked Aadhaar number'}
                {authMode === 'aadhaar-register' && 'Complete your profile to continue'}
              </Typography>

              {/* --- OTP LOGIN FORM --- */}
              {authMode === 'otp-login' && (
                <form onSubmit={showOtpInput ? handleVerifyOtp : handleSendOtp}>
                  {!showOtpInput ? (
                    <input type="tel" placeholder="10-Digit Mobile Number" value={mobile} onChange={(e) => setMobile(e.target.value.replace(/[^0-9]/g, '').slice(0, 10))} style={inputStyle} disabled={loading} required />
                  ) : (
                    <>
                      <input type="text" placeholder="Enter 6-Digit OTP" value={otp} onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))} style={{...inputStyle, letterSpacing: '4px'}} disabled={loading} required />
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                        <Typography variant="caption" sx={{ color: timer === 0 ? '#1b5e20' : 'gray', cursor: timer === 0 ? 'pointer' : 'default', fontWeight: 'bold' }} onClick={timer === 0 ? handleSendOtp : null}>
                          {timer === 0 ? 'Resend OTP' : `00:${timer}s`}
                        </Typography>
                      </Box>
                    </>
                  )}
                  <button type="submit" disabled={loading} style={{ width: '100%', padding: '12px', background: 'linear-gradient(135deg, #1b5e20 0%, #2e7d32 100%)', color: 'white', border: 'none', borderRadius: 8, fontWeight: 'bold', fontSize: '16px', cursor: loading ? 'not-allowed' : 'pointer', marginTop: '30px', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 10 }}>
                    {loading && <CircularProgress size={20} color="inherit" />}
                    {loading ? 'Processing...' : (showOtpInput ? 'Verify & Login' : 'Send OTP')}
                  </button>
                </form>
              )}

              {/* --- AADHAAR VERIFY FORM --- */}
              {authMode === 'aadhaar-verify' && (
                <form onSubmit={handleVerifyAadhaar}>
                  <input type="text" placeholder="XXXX XXXX XXXX" value={aadhaar} onChange={(e) => setAadhaar(e.target.value.replace(/[^0-9]/g, '').slice(0, 12))} style={inputStyle} disabled={loading} required />
                  <button type="submit" disabled={loading} style={{ width: '100%', padding: '12px', background: 'linear-gradient(135deg, #1b5e20 0%, #2e7d32 100%)', color: 'white', border: 'none', borderRadius: 8, fontWeight: 'bold', fontSize: '16px', cursor: loading ? 'not-allowed' : 'pointer', marginTop: '30px', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 10 }}>
                    {loading && <CircularProgress size={20} color="inherit" />}
                    {loading ? 'Verifying...' : 'Verify Aadhaar'}
                  </button>
                </form>
              )}

              {/* --- FARMER REGISTER FORM --- */}
              {authMode === 'aadhaar-register' && (
                <form onSubmit={handleFarmerRegister}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    <Typography variant="caption" sx={{ color: '#2e7d32', fontWeight: 'bold', bgcolor: '#e8f5e9', p: 1, borderRadius: 1, textAlign: 'center' }}>
                      ✓ Aadhaar Verified ({aadhaar.replace(/(\d{4})/g, '$1 ').trim()})
                    </Typography>
                    
                    <input type="text" placeholder="Full Name" value={regData.name} onChange={(e) => setRegData({...regData, name: e.target.value})} style={inputStyle} required />
                    <input type="tel" placeholder="10-Digit Mobile Number" value={regData.phone} onChange={(e) => setRegData({...regData, phone: e.target.value.replace(/[^0-9]/g, '').slice(0, 10)})} style={inputStyle} required />
                    <input type="password" placeholder="Create Password" value={regData.password} onChange={(e) => setRegData({...regData, password: e.target.value})} style={inputStyle} required />
                    
                    <button type="submit" disabled={loading} style={{ width: '100%', padding: '12px', background: 'linear-gradient(135deg, #1b5e20 0%, #2e7d32 100%)', color: 'white', border: 'none', borderRadius: 8, fontWeight: 'bold', fontSize: '16px', cursor: loading ? 'not-allowed' : 'pointer', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 10 }}>
                      {loading && <CircularProgress size={20} color="inherit" />}
                      {loading ? 'Registering...' : 'Register & Login'}
                    </button>
                  </Box>
                </form>
              )}

            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default HomeDashboard;